import React, { useCallback, useReducer } from 'react';
import { ScreenEnum } from '../enums';
import { getSetup } from '../utils/getSetup';
import { getLanguage } from '../utils/getLanguage';
import { AppState } from './interfaces';
import AppStateContext from './context';
import { reducer } from './reducer';
import data from '../data.json';
import * as packageJson from '../../package.json';

export default function AppStateProvider(props: { children: React.ReactNode }) {
	const setup = getSetup();

	// brand, market, kvps, password, device, url, language=de-DE, user-password etc.,
	// label=true, redirect=false, orientation=default|right|down|left, in-frame=true, apk=true, change-device=true
	const params = new URLSearchParams(window.location.search);

	let localStorageStateString = localStorage.getItem('setup');
	let localStorageState = localStorageStateString
		? (JSON.parse(localStorageStateString) as AppState)
		: null;

	if (localStorageState) {
		if (params.get('software-check') === 'true') {
			localStorageState.screen = ScreenEnum.launch;
			localStorageState.softwareCheck = true;
		} else {
			localStorageState = null;
		}
	}

	//for statistics tracking
	localStorage.setItem('SetupVersion', packageJson.version);

	const brandObj = (data.brands as Array<any>).find(
		(b: { name: any }) => b.name === setup?.brand
	);

	const setupName = (
		setup?.devices?.find(d => d.includes(`(${setup.device})`)) || ''
	).split(' ')[0];

	const INITIAL_STATE: AppState = localStorageState || {
		brand: setup?.brand || null,
		market: brandObj
			? (brandObj.markets as Array<any>).find(
					(m: { name: string }) =>
						m.name.toLowerCase() ===
						(setup?.market?.name || undefined)?.toLowerCase()
				) ||
				(brandObj.markets as Array<any>).find(
					(m: { ending: string }) =>
						m.ending.toLowerCase() ===
						setup?.market?.ending.toLowerCase()
				) ||
				null
			: null,
		kvps: [setup?.kvps].map(k => (k && /^\d+$/.test(k) ? k : null))[0],
		password: setup?.password || null,
		devices:
			setupName && setup?.device
				? [`${setupName} (${setup.device})`]
				: null,
		device: setup?.device || null,
		language: getLanguage(data.languages, setup, params.get('language')),
		orientation: setup?.orientation || null,
		show_second_screen: setup?.show_second_screen || false,
		userPassword:
			params.get('user-password') || setup?.userPassword || null,
		screen: ScreenEnum.launch,
		errors: {
			validationError: 0,
			passwordError: 0,
			urlError: 0,
		},
		goToLabel: setup?.goToLabel || false,
		apk:
			params.get('apk') === 'true' ||
			window.location.href.includes('chrome'),
		setup: setup,
		dataJson: data,
		softwareCheck: false,
		offline: !navigator.onLine,
		chromeboxLabel: setup?.chromeboxLabel || false,
		preview: setup?.preview || false,
		expertMode: setup?.expertMode || false,
		pairing: setup?.pairing || false,
		pinpairingDeviceCode: setup?.pinpairingDeviceCode||'',
		pinpairingActivationResponse: setup?.pinpairingActivationResponse ||''
	};

	const [state, dispatch] = useReducer(reducer, INITIAL_STATE);

	return (
		<AppStateContext.Provider
			value={useCallback(
				() => ({
					state,
					dispatch,
				}),
				[state, dispatch]
			)()}
		>
			{props.children}
		</AppStateContext.Provider>
	);
}
